import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Provider,
  ChangeDetectorRef,
  HostBinding
} from "@angular/core";
import { FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import {
  AddressSummary,
  AddressSummaryService
} from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { FullAddress } from "@onsip/common/services/api/apiResponse/address";
import { CdkDragDrop, transferArrayItem, moveItemInArray } from "@angular/cdk/drag-drop";
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from "rxjs";
import { map, startWith, take } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { TranslateService } from "@ngx-translate/core";
import { LocalStorageService } from "@onsip/web/features/shared/services/localStorage/local-storage.service";

export enum MemberType {
  user = "user",
  external = "external",
  telephone_number = "telephone_number",
  all = "all"
}

export enum DisplayMemberType {
  user = "User",
  external = "External SIP",
  telephone_number = "External phone number",
  all = "View all"
}

const CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => SelectMemberDragDropComponent),
  multi: true
};
@Component({
  selector: "onsip-select-member-drag-drop",
  templateUrl: "./select-member-drag-drop.component.html",
  styleUrls: ["./select-member-drag-drop.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CONTROL_VALUE_ACCESSOR],
  standalone: false
})
export class SelectMemberDragDropComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  constructor(
    private addressSummaryService: AddressSummaryService,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private translate: TranslateService,
    private localStorageService: LocalStorageService
  ) {}
  maxItemCount = 10;
  maxExternalNumberCount = 3;
  maxExternalCountReached = false;
  @Input() formControl = new FormControl(<Array<AddressSummary | FullAddress>>[], {
    nonNullable: true
  });
  @Input() isEditComponent = false;
  searchMember = new BehaviorSubject("");
  filterMemberByType = new BehaviorSubject<MemberType>(MemberType.all);
  organizationId!: string;
  onTouched!: Function;
  onChanged!: Function;
  unselectedMembers: Array<AddressSummary | FullAddress> = [];
  selectedMembers: Array<AddressSummary | FullAddress> = [];
  selectedAddressIdSet = new Set();
  prevValue: Array<AddressSummary | FullAddress> = [];
  dragAndDropUpdate = new Subject();

  allMembers$: Observable<Array<AddressSummary | FullAddress>> = combineLatest({
    addresses: this.addressSummaryService.orgState,
    dragAndDropUpdate: this.dragAndDropUpdate.pipe(startWith(""))
  }).pipe(
    map(({ addresses }) => {
      return this.setMembersToArray(addresses);
    })
  );
  filteredMembers$: Observable<Array<AddressSummary | FullAddress>> = combineLatest([
    this.allMembers$,
    this.searchMember,
    this.filterMemberByType
  ]).pipe(
    map(([members, query, type]) => {
      return this.filterMember(members, type).filter(member => {
        return member.name.toLowerCase().includes(query.toLowerCase());
      });
    })
  );

  protected unsubscriber = new Subscription();
  private isHiddenGroupExtNum = false;

  @Input() set maxItem(data: number) {
    this.maxItemCount = data;
  }

  @Input() set searchData(data: string) {
    this.searchMember.next(data);
  }

  @Input() set filterData(type: MemberType) {
    this.filterMemberByType.next(type);
  }

  ngOnInit(): void {
    this.initFields();
    this.addressSummaryService.addressSummaryBrowse();
    this.unsubscriber.add(
      this.filteredMembers$.subscribe(members => {
        this.unselectedMembers = members;
        this.cdRef.markForCheck();
      })
    );
  }

  initFields(): void {
    this.selectedMembers = this.formControl.value;
    this.selectedMembers.map(member => this.selectedAddressIdSet.add(member.addressId));
    if (this.isEditComponent) {
      this.handleExtNumDialog("selected");
    }
  }

  showMemberType(type: MemberType): DisplayMemberType {
    return DisplayMemberType[type];
  }

  trackByAddressId(index: number, item: AddressSummary): string {
    return item.addressId;
  }

  private isExtNumber(member: AddressSummary | FullAddress) {
    return member.type === MemberType.telephone_number;
  }

  checkExtNumLimit(destination: "selected" | "unselected"): void {
    let extNumberMembersLength = (
      this.formControl.value as Array<AddressSummary | FullAddress>
    ).filter(member => this.isExtNumber(member)).length;
    // if moving to unselected column then need to subtract the moving member before comparing to maximum count
    if (destination === "unselected") {
      extNumberMembersLength -= 1;
    }
    if (extNumberMembersLength >= this.maxExternalNumberCount) {
      this.maxExternalCountReached = true;
      return;
    }
    this.maxExternalCountReached = false;
  }

  handleExtNumDialog(destination: "selected" | "unselected"): void {
    this.checkExtNumLimit(destination);
    if (destination === "selected" && !this.isHiddenGroupExtNum && this.maxExternalCountReached) {
      this.unsubscriber.add(
        this.dialog
          .open(ModalMaterialComponent, {
            panelClass: ["mat-typography", "onsip-dialog-universal-style"],
            data: {
              title: this.translate.instant("ONSIP_I18N.PHONE_NUMBER_LIMIT"),
              message: "You can only assign up to three external phone numbers to your group.",
              primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
              secondaryBtnText: this.translate.instant("ONSIP_I18N.DONT_SHOW_MESSAGE_AGAIN"),
              primaryBtnFlat: true
            },
            maxWidth: "340px"
          })
          .afterClosed()
          .pipe(take(1))
          .subscribe(retObj => {
            if (retObj && !retObj.doPrimaryAction) {
              this.localStorageService.setModalsWarningsStorage({
                isHiddenGroupExtNumModal: true
              });
              this.getLocalStorageModalState();
            }
          })
      );
    }
  }

  drop(event: CdkDragDrop<Array<AddressSummary | FullAddress>>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );

      if (this.isExtNumber(event.item.data)) {
        this.handleExtNumDialog(
          event.container.data === this.selectedMembers ? "selected" : "unselected"
        );
      }
      this.cdRef.markForCheck();
    }
    this.formControl.setValue(this.selectedMembers);
    this.selectedAddressIdSet = new Set();
    this.selectedMembers.map(member => this.selectedAddressIdSet.add(member.addressId));
    this.dragAndDropUpdate.next(true);
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  registerOnChange(onChange: Function): void {
    if (this.isEditComponent) {
      return;
    }
    this.unsubscriber.add(
      this.formControl.valueChanges.subscribe(member => {
        onChange(member);
      })
    );
  }

  registerOnTouched(onTouched: Function): void {
    this.onTouched = onTouched;
  }

  compareAddressIds(prev: Array<string>, cur: Array<string>) {
    if (prev.length !== cur.length) {
      return false;
    }
    for (let i = 0; i < prev.length; i++) {
      if (prev[i] !== cur[i]) {
        return false;
      }
    }
    return true;
  }

  writeValue(value: Array<AddressSummary | FullAddress>): void {
    if (this.isEditComponent) {
      return;
    }
    this.formControl.setValue(value);
  }

  private setMembersToArray(
    addresses: Array<AddressSummary | FullAddress>
  ): Array<AddressSummary | FullAddress> {
    return addresses
      .filter(
        address =>
          // we only allow these three address types to be added to groups and addresses that aren't already part of the group
          !this.selectedAddressIdSet.has(address.addressId) &&
          (address.type === MemberType.user ||
            address.type === MemberType.telephone_number ||
            address.type === MemberType.external)
      )
      .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
  }

  private filterMember(
    members: Array<AddressSummary | FullAddress>,
    type: MemberType
  ): Array<AddressSummary | FullAddress> {
    if (type === MemberType.all) {
      return members;
    }
    return members.filter(member => member.type === type);
  }

  private getLocalStorageModalState(): void {
    this.localStorageService.getModalWarnings().then(types => {
      this.isHiddenGroupExtNum = !!types.isHiddenGroupExtNumModal;
    });
  }
}
